import { Button, Col, Icon, Row } from '@zoom/components';
import { FormApi } from 'final-form';
import { TFunction } from 'i18next';
import React from 'react';
import { Field, Form, FormRenderProps, FormSpy } from 'react-final-form';
import { Link } from 'react-router-dom';

import { DatePickerField } from './DatePicker';
import { Authenticated, AuthorizationConsumer, PermissionsType } from '../app/AuthorizationProvider';
import { max255chars } from '../utils/validations';
import { finalFormDeBouncedInputHelper } from './Inputs/DeBouncedInput';
import { Separator } from './layout/Separator';

interface FilterFormProps extends FormRenderProps {
  t: TFunction;
  createButtonLabel: string;
  createButtonPath: string;
  requiredPermissionsForCreate: PermissionsType[];
}

const FilterForm: React.FC<FilterFormProps> = ({
  t,
  handleSubmit,
  createButtonPath,
  values,
  requiredPermissionsForCreate,
  createButtonLabel,
}) => {
  const createButton = ({ userHasPermissions }: Authenticated) =>
    userHasPermissions(requiredPermissionsForCreate) && (
      <>
        <Col sm="auto">
          <Button color="primary" data-test-id="list-filter:button-create" tag={Link} to={createButtonPath}>
            {t(createButtonLabel)}
          </Button>
        </Col>
        <Separator />
      </>
    );

  return (
    <form name="filter-form">
      <FormSpy
        subscription={{ dirty: true, modified: true, values: true }}
        onChange={({ modified }) => {
          // Do not fire on initial render, when updating state is causing React error
          if (modified && Object.values(modified).filter((filteredValueModified) => filteredValueModified).length) {
            handleSubmit();
          }
        }}
      />
      <Row>
        <AuthorizationConsumer>{createButton}</AuthorizationConsumer>
        <Col sm="auto">
          <Field
            name="search"
            validate={max255chars}
            render={finalFormDeBouncedInputHelper({
              label: t('filter.searchByName'),
              dataTestId: 'list-filter:input-search',
              type: 'text',
            })}
          />
        </Col>
        <Col sm="auto">
          <Field
            name="startDateTime"
            component={DatePickerField({
              dateFormat: t('date.formats.default'),
              label: t('date.dateFrom'),
              startDate: values.startDateTime,
              endDate: values.endDateTime,
              maxDate: values.endDateTime,
              selectsStart: true,
            })}
          />
        </Col>
        <Col sm="auto">
          <Field
            name="endDateTime"
            component={DatePickerField({
              dateFormat: t('date.formats.default'),
              label: t('date.dateTo'),
              startDate: values.startDateTime,
              endDate: values.endDateTime,
              minDate: values.startDateTime,
              selectsEnd: true,
            })}
          />
        </Col>
        <FormSpy subscription={{ dirty: true }}>
          {({ dirty, form: { reset } }) =>
            dirty && (
              <Col sm="auto">
                <Button
                  kind="icon-bordered"
                  data-test-id="list-filter:button-refresh"
                  onClick={() => reset({})}
                  title={t('filter.resetFilter')}
                >
                  <Icon kind="cross" size={16} color="black2" />
                </Button>
              </Col>
            )
          }
        </FormSpy>
      </Row>
    </form>
  );
};

export interface ListFilterProps extends React.PropsWithChildren {
  t: TFunction;
  onSubmit: (values: ListFilterResults, form: FormApi) => void;
  createButtonPath: string;
  createButtonLabel: string;
  requiredPermissionsForCreate: PermissionsType[];
  preFilteredDate?: Date;
}

export interface ListFilterResults {
  search?: string;
  startDateTime?: string;
  endDateTime?: string;
}

export const ListFilter: React.FC<ListFilterProps> = (props) => {
  const { t, onSubmit, requiredPermissionsForCreate, preFilteredDate, ...rest } = props;

  const renderForm = (formProps: FormRenderProps) => (
    <FilterForm t={t} {...formProps} requiredPermissionsForCreate={requiredPermissionsForCreate} {...rest} />
  );

  return (
    <Form
      onSubmit={onSubmit}
      render={renderForm}
      initialValues={
        preFilteredDate
          ? {
              endDateTime: preFilteredDate,
              startDateTime: preFilteredDate,
            }
          : {}
      }
    />
  );
};
